import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../Button/Button';
import { DEVICE_MODAL } from '../DeviceSelectModal/DeviceSelectModal';
import { useCallState } from '../../contexts/CallProvider';
import { useUIState } from '../../contexts/UIStateProvider';
import { useResponsive } from '../../hooks/useResponsive';
import { useMediaDevices } from '../../contexts/MediaDeviceProvider';
import { TrayButton } from './Tray';
import { CallTimer } from './CallTimer';
import { PEOPLE_ASIDE } from '../Aside/PeopleAside';
import { NETWORK_ASIDE } from '../Aside/NetworkAside';
import { useScreenShare } from '../../contexts/ScreenShareProvider';
import DailyIframe from '@daily-co/daily-js';
import { LINK_MODAL } from '../../../Call/LinkModal';
import { LEAVE_MODAL } from '../../../Call/LeaveModal';
import { theme } from '../../../../../../common/styles/theme';
import { Paper } from '../../../../../../common/components/Paper/Paper';
import { IBasicTrayProps } from './types';
import { Box, Menu, Stack } from '@mui/material';
import { IconMenuItem } from '../../../../../../common/components/IconMenuItem/IconMenuItem';
import { mobileSheetProps, mobileSheetSx } from '../../../../../../common/components/MobileSheet/mobileSheet';
import { BlurPersonIcon } from '../../../../../../../icons/BlurPersonIcon';
import { useRecording, RECORDING_RECORDING } from '../../contexts/RecordingProvider';
import { Wording } from '../../../ConferenceSettings/wording';
import { TConferenceSaveLevel } from '../../../../../../common/rest/conference/conferenceSaveLevel';
import { PastLessonsButtonHOC } from '../../../PastLessons/hocs/PastLessonsButtonHOC';

const IconCameraOff = require('!!@svgr/webpack!../../icons/camera-off-md.svg').default;
const IconCameraOn = require('!!@svgr/webpack!../../icons/camera-on-md.svg').default;
const IconLeave = require('!!@svgr/webpack!../../icons/leave-md.svg').default;
const IconMicOff = require('!!@svgr/webpack!../../icons/mic-off-md.svg').default;
const IconMicOn = require('!!@svgr/webpack!../../icons/mic-on-md.svg').default;
const IconMore = require('!!@svgr/webpack!../../icons/more-md.svg').default;
const IconNetwork = require('!!@svgr/webpack!../../icons/network-md.svg').default;
const IconPeople = require('!!@svgr/webpack!../../icons/people-md.svg').default;
const IconSettings = require('!!@svgr/webpack!../../icons/settings-md.svg').default;
const IconShare = require('!!@svgr/webpack!../../icons/share-sm.svg').default;
const IconLink = require('!!@svgr/webpack!../../icons/link.svg').default;
const IconViewSpeaker = require('!!@svgr/webpack!../../icons/speaker-view-md.svg').default;
const IconViewGrid = require('!!@svgr/webpack!../../icons/grid-md.svg').default;
const IconRecord = require('!!@svgr/webpack!../../icons/record-md.svg').default;

// Mobile browsers can't capture the screen (no getDisplayMedia); viewing others' shares still works.
const CAN_SHARE_SCREEN = DailyIframe.supportedBrowser().supportsScreenShare;
const DEVICES_WAIT_MS = 3000;

// Auto-opened popups are once per call, not per tray mount: the tray remounts when the user comes
// back from a lesson page (Preview) and must not open Settings / Link again. Keyed by call object.
const autoOpenedByCall = new WeakMap<object, Set<string>>();
const markAutoOpened = (callObject: object | null | undefined, name: string): boolean => {
	if (!callObject) return false;
	let opened = autoOpenedByCall.get(callObject);
	if (!opened) {
		opened = new Set();
		autoOpenedByCall.set(callObject, opened);
	}
	if (opened.has(name)) return false;
	opened.add(name);
	return true;
};

// First visible of the given tray buttons (e.g. Settings, or More when it's folded away on mobile).
const getTrayButton = (...names: string[]) => names
	.map((name) => document.querySelector<HTMLElement>(`.tray-button--${name} .MuiIconButton-root`))
	.find(Boolean) || null;

export const BasicTray = ({
	isToken,
	startedAt,
	onLeave,
	onOpen,
}: IBasicTrayProps) => {
	const ref = useRef(null);
	const responsive = useResponsive();
	const isMobile = responsive.isMobile();
	// mobile: the secondary buttons live in a bottom sheet behind «More», so the tray fits the screen
	const [moreOpen, setMoreOpen] = useState(false);
	const fromMore = (action: () => void) => () => {
		setMoreOpen(false);
		action();
	};
	const { callObject, leave, saveLevel, canBlurBackground, backgroundBlur, backgroundBlurPending, setBackgroundBlur } = useCallState();
	const {
		customTrayComponent,
		openModal,
		toggleAside,
		showParticipantsBar,
		setShowParticipantsBar,
		setShowAside,
		autoOpenModal,
		resetAutoModals,
	} = useUIState();
	const { isCamMuted, isMicMuted, toggleMic, cams, mics } = useMediaDevices();
	const [micLabel, setMicLabel] = useState('Mic');
	const [camLabel, setCamLabel] = useState('Cam');
	useEffect(() => {
		const selectedMic = mics?.find((i: any) => i.selected);
		if (!selectedMic) return;
		const micLabel = selectedMic.device.label.replace(/\s*\([^()]*\)\s*$/, '') || 'Mic';
		setMicLabel(micLabel);
	}, [isMicMuted, mics]);
	useEffect(() => {
		const selectedCam = cams?.find((i: any) => i.selected);
		if (!selectedCam) return;
		const camLabel = selectedCam.device.label.replace(/\s*\([^()]*\)\s*$/, '') || 'Cam';
		setCamLabel(camLabel);
	}, [isCamMuted, cams]);

	// Settings should go before link, so link waits until devices are known — but at most
	// DEVICES_WAIT_MS (they may never load, e.g. no camera); devices arriving later queue after link.
	const autoOpenLink = () => {
		if (!isToken || !markAutoOpened(callObject, LINK_MODAL)) return;
		autoOpenModal(LINK_MODAL, () => getTrayButton('link'));
	};

	useEffect(() => {
		onOpen();
		const timeout = setTimeout(autoOpenLink, DEVICES_WAIT_MS);
		return () => {
			clearTimeout(timeout);
			resetAutoModals();
		};
	}, []);

	useEffect(() => {
		if (!mics?.length || !cams?.length) return;
		const countUnique = (list: any[]) => new Set(
			list
				.map((i: any) => i.device?.groupId || i.device?.deviceId)
				.filter((id: string) => id && id !== 'default' && id !== 'communications')
		).size;
		if ((countUnique(mics) > 1 || countUnique(cams) > 1) && markAutoOpened(callObject, DEVICE_MODAL)) {
			autoOpenModal(DEVICE_MODAL, () => getTrayButton('settings', 'more'));
		}
		autoOpenLink();
	}, [mics, cams]);

	const { screens, isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare();
	// Same rule as Room: any active share forces speaker view.
	const isSpeakerView = showParticipantsBar || screens.length > 0;

	const toggleCamera = (newState: any) => {
		if (!callObject) return false;
		return callObject.setLocalVideo(newState);
	};

	// through the provider, so a stream we captured ourselves (shareCurrentTab) is stopped too
	const toggleShare = () => {
		if (isSharingScreen) {
			stopScreenShare();
		} else {
			startScreenShare();
		}
	};

	const onLeaveHandle = async () => {
		if (isToken) {
			openModal(LEAVE_MODAL);
		} else {
			onLeave();
			leave();
			setShowAside(null);
		}
	};

	const onToggleView = () => {
		setShowParticipantsBar(!showParticipantsBar);
	}

	const { recordingState } = useRecording();
	const isRec = recordingState===RECORDING_RECORDING;
	// "Recording starts" is a transient pre-recording state, not the real saveLevel value yet — the
	// button stays inert there; once it shows the real value, clicking it opens the same settings
	// popup as the "Settings" button above, so the indicator doubles as an entry point to change it.
	const isRecordingStarting = saveLevel !== 'nothing' && !isRec;
	const recordingLabel = saveLevel === 'nothing'
		? Wording.SAVE_LEVEL_TRAY_LABELS.nothing
		: isRec
			? `Recording: ${Wording.SAVE_LEVEL_TRAY_LABELS[saveLevel as TConferenceSaveLevel]}`
			: 'Recording starts';

	return (
		<Paper
			sx={{
				p: theme.spacing(1, 2),
				flex: `0 0 auto`,
				width: 1,
				overflowY: 'visible',
				overflowX: 'auto',

				'@media (max-width: 600px)': {
					px: 1,
				},
			}}
		>
			<Stack
				flexDirection={'row'}
				gap={isMobile ? .5 : 1}
				sx={{
					width: 'fit-content',
					m: '0 auto',
				}}
			>
				<TrayButton
					compact={isMobile}
					label={isCamMuted ? 'Cam off' : camLabel}
					onClick={() => toggleCamera(isCamMuted)}
					orange={isCamMuted}
					{...(canBlurBackground && !isMobile ? {
						side: {
							title: backgroundBlur ? 'Background blurred' : 'Blur background',
							icon: <BlurPersonIcon />,
							active: !!backgroundBlur,
							pending: !!backgroundBlurPending,
							onClick: () => setBackgroundBlur(!backgroundBlur),
						},
					} : {})}
				>
					{isCamMuted ? <IconCameraOff /> : <IconCameraOn />}
				</TrayButton>
				<TrayButton
					compact={isMobile}
					label={isMicMuted ? 'Mic off' : micLabel}
					onClick={toggleMic}
					orange={isMicMuted}
				>
					{isMicMuted ? <IconMicOff /> : <IconMicOn />}
				</TrayButton>

				{isMobile
					? <>
						<TrayButton className="tray-button--more" compact label="More" onClick={() => setMoreOpen(true)}>
							<IconMore />
						</TrayButton>
						<Menu
							open={moreOpen}
							onClose={() => setMoreOpen(false)}
							{...mobileSheetProps}
							sx={mobileSheetSx}
						>
							<IconMenuItem title={'Settings'} onClick={fromMore(() => openModal(DEVICE_MODAL))}>
								<IconSettings />
							</IconMenuItem>
							{isToken &&
								<IconMenuItem title={'Link'} onClick={fromMore(() => openModal(LINK_MODAL))}>
									<IconLink />
								</IconMenuItem>
							}
							{/* a share forces speaker view, so there's nothing to switch then */}
							{screens.length === 0 &&
								<IconMenuItem title={isSpeakerView ? 'Grid view' : 'Speaker view'} onClick={fromMore(onToggleView)}>
									{isSpeakerView ? <IconViewGrid /> : <IconViewSpeaker />}
								</IconMenuItem>
							}
							{CAN_SHARE_SCREEN &&
								<IconMenuItem title={isSharingScreen ? 'Stop sharing' : 'Share screen'} onClick={fromMore(toggleShare)}>
									<IconShare />
								</IconMenuItem>
							}
							{canBlurBackground &&
								<IconMenuItem
									title={backgroundBlur ? 'Unblur background' : 'Blur background'}
									onClick={fromMore(() => { if (!backgroundBlurPending) setBackgroundBlur(!backgroundBlur); })}
								>
									<BlurPersonIcon />
								</IconMenuItem>
							}
						</Menu>
					</>
					: <>
						<TrayButton className="tray-button--settings" label="Settings" onClick={() => openModal(DEVICE_MODAL)}>
							<IconSettings />
						</TrayButton>

						<TrayButton
							disabled={screens.length > 0}
							label={isSpeakerView ? 'Speaker view' : 'Grid view'}
							onClick={onToggleView}
						>
							{isSpeakerView
								? <IconViewSpeaker />
								: <IconViewGrid />
							}
						</TrayButton>

						{CAN_SHARE_SCREEN &&
							<TrayButton
								label={isSharingScreen ? 'Stop sharing' : 'Share screen'}
								onClick={toggleShare}
								green={isSharingScreen}
							>
								<IconShare />
							</TrayButton>
						}
						{CAN_SHARE_SCREEN &&
							<PastLessonsButtonHOC />
						}
					</>
				}

				{customTrayComponent}

				{isToken && !isMobile &&
					<TrayButton className="tray-button--link" label="Link" onClick={() => openModal(LINK_MODAL)}>
						<IconLink />
					</TrayButton>
				}

				<Box
					className="divider"
					sx={{
						m: theme.spacing(isMobile ? .5 : 1),
					}}
				/>

				<TrayButton
					compact={isMobile}
					className={isRec ? 'rec' : ''}
					plain
					label={<>
						{recordingLabel}
						{!!startedAt && <><br /><CallTimer startedAt={startedAt} /></>}
					</>}
					onClick={isRecordingStarting ? () => {} : () => openModal(DEVICE_MODAL, { autoOpenSaveLevel: true })}
					sx={{
						...(isRecordingStarting ? { pointerEvents: 'none' } : {}),
						// the timer box has its own padding, so this label doesn't fit the 3-line height
						'.tray-button-label': {
							height: 'auto',
							WebkitLineClamp: 'unset',
						},
						'&.rec': {
							'.MuiButtonBase-root': {
								color: theme.palette.error.main,
							},
						},
					}}
				>
					<IconRecord />
				</TrayButton>

				<TrayButton
					compact={isMobile}
					label={isToken ? 'Finish' : 'Leave'}
					onClick={onLeaveHandle} orange>
					<IconLeave />
				</TrayButton>
			</Stack>

		</Paper>
	);
};
